/** biome-ignore-all lint/suspicious/noExplicitAny: no any */
import type {BaseQueryFn, FetchArgs, FetchBaseQueryError} from '@reduxjs/toolkit/query';
import isObject from 'lodash/isObject';
import {showCaptcha} from '~library/utils/auth/utils';

/*
 * Use the following like:
 *
 * export const api = createApi({
 *   reducerPath: 'apiName',
 *   baseQuery: customBaseQuery(),
 *
 * or provider a basePath:
 *
 *   baseQuery: customBaseQuery({basePath: '/ajax/cart/v1'}),
 *
 * These functions both return the BaseQueryFn required by `createApi`'s  `baseQuery`
 * param, so you must call them to get the query function.
 *
 */

type BaseQueryArgs = {
  url: string;
  method?: string;
  body?: any;
  headers?: Record<string, string | undefined> | Headers | string[][];
};

type CustomBaseQueryArgs = {
  headers?: Record<string, string | undefined> | Headers | string[][];
  basePath: string;
};

export const customBaseQuery =
  (
    queryOptions?: CustomBaseQueryArgs
  ): BaseQueryFn<
    {
      url: string;
      method?: string;
      body?: any;
      params?: any;
      headers?: Record<string, string | undefined> | Headers | string[][];
    },
    unknown,
    unknown
  > =>
  async ({url, method = 'GET', body, headers = {}}: BaseQueryArgs) => {
    try {
      const basePath = queryOptions?.basePath || '';
      const headersFromOptions = queryOptions?.headers || {};
      const options = {
        body: undefined,
        method,
        credentials: 'include',
        headers: {
          ...headers,
          ...headersFromOptions,
          'X-CSRFToken': evite.cookie('csrftoken') || '',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };
      if (body) {
        options.body = isObject(body) ? JSON.stringify(body) : body;
      }
      const response = await evite.fetch(window.location.origin + basePath + url, options);
      const data = await response.json();
      return response.ok ? {data} : {error: {status: response.status, data}};
    } catch (error) {
      return {
        error: {
          status: 500,
          data: error,
        },
      };
    }
  };

const DEFAULT_RETRY_COUNT = 3;

type CustomBaseQueryWithCaptchaArgs = CustomBaseQueryArgs & {
  retryCount?: number;
};

export const customBaseQueryWithCaptcha =
  (
    queryOptions?: CustomBaseQueryWithCaptchaArgs
  ): BaseQueryFn<FetchArgs, unknown, FetchBaseQueryError> =>
  async (args, api, extraOptions) => {
    const basePath = queryOptions?.basePath || '';
    const retryCount = queryOptions?.retryCount || DEFAULT_RETRY_COUNT;
    const baseQuery = customBaseQuery({...queryOptions, basePath});

    let result: any = null;
    let attempts = 0;
    let captchaResponse = null;
    while (attempts < retryCount) {
      attempts++;
      const queryArgs = structuredClone(args);
      if (captchaResponse) {
        queryArgs.body = queryArgs?.body || {};
        queryArgs.body.captcha = captchaResponse;
      }
      result = await baseQuery(queryArgs, api, extraOptions);
      if (!result.error) break; // No error
      switch (result.error.status) {
        case 412: {
          // Handle captcha, and do not auto-return (i.e. allow retry)
          const payload = result.error.data?.error?.data?.detail || result.error.data;
          captchaResponse = await showCaptcha({
            ...result.error,
            data: payload,
          });
          if (attempts >= 3) {
            return result;
          }
          break;
        }
        default:
          break;
      }
    }
    return result;
  };
